/* eslint-disable unicorn/prefer-array-some */
/* eslint-disable no-plusplus */
/* eslint-disable no-unsafe-optional-chaining */
import { ChevronCell, Row } from '@silevis/reactgrid';

import { blockOutputDisplayText } from 'utils/scaleBlockOutputPercentageValues';

// Plan's table never stretches value columns to fill leftover container
// width — they size to content (`whiteSpace: nowrap`, no minW) and stay
// compact even on a wide screen. Stretching to fill (the old behaviour here)
// made each cell balloon to fill whatever space was left when there were
// only a handful of columns, which read as "too big" next to Plan. A fixed
// width keeps every value column the same compact size Plan's columns settle
// at in practice, regardless of how much space the container has.
const VALUE_COLUMN_WIDTH = 130;

const calculateWidth = (
  totalElements: number,
  totalWidth: number,
  actionColumnWidth: number = 60,
) => {
  const minColumnWidth = 150;
  if (!totalElements || !totalWidth) return minColumnWidth;
  const minTotalWidth = totalElements * minColumnWidth + actionColumnWidth;
  return totalWidth > minTotalWidth
    ? (totalWidth - actionColumnWidth) / totalElements
    : minColumnWidth;
};

// 34px matches DimensionsRevamp's `ROW_HEIGHT` — with the 12px Comfortaa/
// Roboto text now used throughout, 40px left too much empty vertical space
// per row next to Plan/Dimensions' denser tables.
const ROW_HEIGHT = 34;

// `checkboxState` is undefined for tables that don't support row selection
// (the checkbox column/cells are only added when it's passed), so existing
// callers are unaffected.
const dimensionalHeaderRow = (
  tableProperty_: any,
  checkboxState?: { checked: boolean; indeterminate: boolean },
) => ({
  rowId: 'header',
  height: ROW_HEIGHT,
  cells: [
    ...(checkboxState
      ? [
          {
            type: 'checkbox',
            checked: checkboxState.checked,
            isIndeterminate: checkboxState.indeterminate,
            nonEditable: false,
            className: 'header-cell checkbox-column',
          },
        ]
      : []),
    {
      type: 'header',
      text: 'Name',
      colType: 'header',
      nonEditable: true,
      className: 'header-cell header-first first-column',
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any, index: any) => {
          const row = {
            type: 'header',
            colType: 'header',
            nonEditable: true,
            text: data?.name,
            className: `header-cell ${
              index === tableProperty_?.length - 1 ? ' last-index header-last ' : ''
            }${data?.isActual ? 'actuals-cell' : ''}`,
          };
          return row;
        })
      : []),
  ],
});

const dimensionalFooterRow = (tableProperty_: any, hasCheckboxColumn?: boolean) => ({
  rowId: 'footer',
  height: ROW_HEIGHT,
  cells: [
    ...(hasCheckboxColumn
      ? [
          {
            type: 'text' as const,
            text: '',
            nonEditable: true,
            className: 'footer checkbox-column',
          },
        ]
      : []),
    {
      type: 'addRow',
      text: '',
      className: 'footer footer-first first-column',
      showText: true,
      nonEditable: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((_: any, index_: number) => {
          const row = {
            type: 'text',
            text: '',
            nonEditable: true,
            className: `${
              index_ === tableProperty_?.length - 1 ? ' last-index footer-last ' : ''
            } footer`,
            hasFormulaError: false,
          };
          return row;
        })
      : []),
  ],
});

const getDimensionalRows = (
  tableItemsProperty_: any[],
  tableProperty_: any[],
  modelCurrency?: string,
) => {
  // Memoize header and footer rows since they don't change often
  const headerRow = dimensionalHeaderRow(tableProperty_);
  const footerRow = dimensionalFooterRow(tableProperty_);
  // Build cells separately to improve readability and performance
  const buildCells = (item: any, index: number, properties: any[], currency?: string) => {
    const nameCell = {
      type: item?.chevronType ? 'chevron' : 'text',
      isExpanded: false,
      hasChildren: item?.chevronType,
      parentId: item?.parentId,
      placeholder: 'Enter value',
      currencyType: currency,
      text: item?.name ?? '',
      property: 'name',
      rows: tableItemsProperty_?.length,
      hasFormulaError: item?.warning?.length > 0,
      hasFormulaErrorMessage: item?.warning,
      className: `name ${item?.parentId ? 'chevron-child' : ''} ${
        item?.warning?.length > 0 ? 'formula-error ' : ''
      }`,
      isNewCell: !!item?.isNewCell,
      indicatorType: item?.indicator_type,
      nonEditable: !!item?.parentId,
      index,
      ...item?.cellData,
    };

    const valueCells = properties?.map((data: any, index_: number) => {
      const value = item?.total_values?.find((data_: any) => data_?.name === data?.name);
      return {
        type: 'text',
        isNewCell: !!item?.isNewCell,
        rows: tableItemsProperty_?.length,
        text: item?.isNewCell
          ? '0'
          : value
          ? blockOutputDisplayText(value?.value, item?.type)
          : '0',
        format: item?.type,
        tableName: 'block',
        currencyType: currency,
        index,
        data,
        nonEditable: true,
        className: `${item?.parentId ? ' chevron-child ' : ''} ${
          index_ === properties?.length - 1 ? ' last-index ' : ''
        } ${item?.warning?.length > 0 ? 'formula-error ' : ''} ${
          data?.isActual ? 'actuals-cell' : ''
        }`,
        hasFormulaError: false,
      };
    });

    // Per-row delete is no longer a persistent "Action" column (matching
    // DimensionsRevamp) — deletion now happens through row selection +
    // BuilderModeRevamp's `SelectionActionBar`.
    return [nameCell, ...(valueCells || [])];
  };
  // Process items in chunks for better performance
  const processItems = (items: any[]) => {
    return items.map((item: any, index: number) => ({
      rowId: item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`,
      height: 40,
      itemId: item?.id ?? item?.indicatorId,
      itemName: item?.name,
      reorderable: !item?.parentId,
      isNewCell: !!item?.isNewCell,
      cells: buildCells(item, index, tableProperty_, modelCurrency),
    }));
  };

  return [
    headerRow,
    ...(tableItemsProperty_?.length ? processItems(tableItemsProperty_) : []),
    footerRow,
  ];
};

// Add memoization utility
const memoize = <T extends (...parameters: any[]) => any>(targetFunction: T) => {
  const cache = new Map();
  return (...parameters: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(parameters);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = targetFunction(...parameters);
    cache.set(key, result);
    return result;
  };
};

// Optimize getTextWidth with memoization
const getTextWidth = memoize((text: string) => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  const metrics = context.measureText(text);
  return metrics.width;
});

const getDimensionalColumns = (
  tableProperty_: any,
  tableReference: any,
  plannerBlock?: any,
  withCheckboxColumn?: boolean,
) => {
  // Find the maximum width of the content in the "Name" column. 211px floor
  // matches PlanRevamp's `PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH` exactly.
  const nameColumnContent = tableProperty_?.map((data: any) => data.name) || [];
  const maxNameWidth = Math.max(...nameColumnContent.map((name: any) => getTextWidth(name)), 211);
  const columnWidth = VALUE_COLUMN_WIDTH;
  return [
    ...(withCheckboxColumn
      ? [{ columnId: 'checkbox', width: 36, resizable: false, reorderable: false }]
      : []),
    {
      columnId: 'name',
      width:
        plannerBlock?.indicator_column_size !== null
          ? plannerBlock?.indicator_column_size
          : maxNameWidth,
      resizable: true,
      reorderable: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any) => {
          const row = {
            columnId: `${data?.id}`,
            width: columnWidth,
            resizable: true,
            reorderable: true,
          };
          return row;
        })
      : []),
  ];
};

const getColumns = (
  tableProperty_: any,
  tableReference: any,
  actionColumnWidth: number = 60,
  // Opt-in only — the Property table's "Block indicators" column was
  // truncating every name behind an ellipsis at the same width as every
  // other column. Existing callers (Excel/MonthlyView/UnExpand tables) don't
  // pass this, so their layout is unchanged.
  firstColumnMinWidth?: number,
  // Opt-in — per-row delete is no longer a persistent "Action" column
  // (matching the Data tab/Dimensions page); deletion now happens through
  // row selection + a SelectionActionBar, which needs this checkbox column.
  withCheckboxColumn?: boolean,
) => {
  const columnWidth =
    tableProperty_?.length && tableReference
      ? calculateWidth(tableProperty_.length, tableReference, actionColumnWidth)
      : 200;
  return [
    ...(withCheckboxColumn
      ? [{ columnId: 'checkbox', width: 36, resizable: false, reorderable: false }]
      : []),
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any, index: number) => {
          const row = {
            columnId: `${data?.id}`,
            width:
              index === 0 && firstColumnMinWidth
                ? Math.max(columnWidth, firstColumnMinWidth)
                : columnWidth,
            resizable: true,
            reorderable: true,
          };
          return row;
        })
      : []),
  ];
};

const findChevronCell = (row: Row) =>
  row.cells.find((cell) => cell.type === 'chevron') as ChevronCell | undefined;

const getExpandedRows = (rows: Row[]): Row[] => {
  if (!rows?.length) return [];

  const expandedRowIds = new Set<string | number>();
  const collapsedParentIds = new Set<string | number>();

  // First pass: identify expanded/collapsed states
  rows.forEach((row) => {
    const chevronCell = findChevronCell(row);
    if (chevronCell) {
      if (chevronCell.isExpanded) {
        expandedRowIds.add(row.rowId!);
      } else {
        collapsedParentIds.add(row.rowId!);
      }
    }
  });

  // Second pass: filter rows
  return rows.filter((row) => {
    const { parentId: initialParentId } = findChevronCell(row) || {};
    if (!initialParentId) return true;

    // Check if any parent is collapsed
    const isParentCollapsed = (startParentId: string | number): boolean => {
      let currentParentId = startParentId;

      const findParentChevron = (id: string | number) => {
        const { rowId } = rows.find((r) => r.rowId === id) || {};
        return rowId ? findChevronCell(rows.find((r) => r.rowId === rowId)!) : undefined;
      };

      while (currentParentId) {
        if (collapsedParentIds.has(currentParentId)) return true;
        const parentChevron = findParentChevron(currentParentId);
        if (!parentChevron?.parentId) break;
        currentParentId = parentChevron.parentId;
      }
      return false;
    };

    return !isParentCollapsed(initialParentId);
  });
};

const buildTree = (rows: Row[]): Row[] => {
  if (!rows?.length) return [];

  // Create Maps for faster lookups
  const rowMap = new Map<string | number, Row>();
  const childrenMap = new Map<string | number, Row[]>();

  // First pass: Build maps
  rows.forEach((row) => {
    if (row.rowId) {
      rowMap.set(row.rowId, row);

      const chevronCell = findChevronCell(row);
      if (chevronCell?.parentId) {
        const parentChildren = childrenMap.get(chevronCell.parentId) || [];
        parentChildren.push(row);
        childrenMap.set(chevronCell.parentId, parentChildren);
      }
    }
  });

  // Second pass: Process chevron cells
  return rows.map((row) => {
    const chevronCell = findChevronCell(row);
    if (chevronCell && !chevronCell.parentId) {
      const rowChildren = childrenMap.get(row.rowId) || [];
      chevronCell.hasChildren = rowChildren.length > 0;

      if (chevronCell.hasChildren) {
        const startIndent = 0;
        const processChildren = (parentId: string | number, level: number) => {
          const nestedChildren = childrenMap.get(parentId) || [];
          nestedChildren.forEach((childRow) => {
            const childChevron = findChevronCell(childRow);
            if (childChevron) {
              childChevron.indent = level;
              childChevron.hasChildren = (childrenMap.get(childRow.rowId) || []).length > 0;
              if (childChevron.hasChildren) {
                processChildren(childRow.rowId!, level + 1);
              }
            }
          });
        };
        processChildren(row.rowId!, startIndent + 1);
      }
    }
    return row;
  });
};

const handleLocationChanged = (event: any, setFocus: any) => {
  setFocus(event);
  return true;
};

/** 0 -> A, 25 -> Z, 26 -> AA — spreadsheet column labelling for the formula bar. */
const toColumnLetters = (index: number): string => {
  let remaining = index;
  let label = '';
  while (remaining >= 0) {
    label = String.fromCodePoint((remaining % 26) + 65) + label;
    remaining = Math.floor(remaining / 26) - 1;
  }
  return label;
};

/**
 * Excel-style reference ("B5") for the formula bar's name box. The leading
 * checkbox column isn't addressable, so the first real column is A, and row 1
 * is the first data row (the header row isn't counted).
 */
const buildCellReference = (
  columns: any[] | undefined,
  rows: any[] | undefined,
  columnId: any,
  rowId: any,
): string => {
  const columnIndex = columns?.findIndex((column: any) => column.columnId === columnId) ?? -1;
  const addressableIndex = columnIndex - 1;
  if (addressableIndex < 0) return '';

  const rowIndex = rows?.findIndex((row: any) => row.rowId === rowId) ?? -1;
  if (rowIndex <= 0) return '';

  return `${toColumnLetters(addressableIndex)}${rowIndex}`;
};

// Optimize reorderArray with more efficient array operations
const reorderArray = <T extends { id: number | string }>(
  array: T[],
  indices: number[],
  targetPosition: number,
): (string | number)[] => {
  // Guard against out-of-range indices (e.g. -1 from a findIndex miss),
  // which previously produced `undefined` entries and crashed on `.id`.
  const validIndices = indices.filter((index) => index >= 0 && index < array.length);
  const movedElements = new Set(validIndices.map((index) => array[index]));
  const targetIndex =
    validIndices.length === 0
      ? targetPosition
      : Math.min(...validIndices) < targetPosition
      ? targetPosition + 1
      : targetPosition - validIndices.filter((index) => index < targetPosition).length;

  const result = array
    .filter((_, index) => !validIndices.includes(index))
    .reduce((accumulator, item, index) => {
      if (index === targetIndex) {
        return [...accumulator, ...Array.from(movedElements), item];
      }
      return [...accumulator, item];
    }, [] as T[]);

  if (targetIndex >= array.length) {
    result.push(...Array.from(movedElements));
  }

  // Only real indicator ids reach the reorder endpoint. `array` is the flat
  // table data, which also carries dimension/child rows whose `id` can be a
  // composite string or missing entirely — those were going into the request
  // body as `undefined`, making it a malformed reorder payload.
  return result
    .map((item) => item?.id)
    .filter((id): id is string | number => id !== null && id !== undefined && id !== '');
};

const handleCanReorderRows = (targetRowId: any) => {
  return typeof targetRowId === 'number';
};

const handleCellChange = (changes: any, columns: any, setRows: any) => {
  setRows((rows_: any) => {
    changes.forEach((change: any) => {
      const changeRowIndex = rows_.findIndex((element: any) => element.rowId === change.rowId);
      const changeColumnIndex = columns.findIndex(
        (element: any) => element.columnId === change.columnId,
      );
      rows_[changeRowIndex].cells[changeColumnIndex] = change?.newCell;
    });
    return [...rows_];
  });
};

const handleColumnResize = (columnId: any, width: number, setColumns: any) => {
  setColumns((column: any) => {
    const columnIndex = column.findIndex((element: any) => element.columnId === columnId);
    const resizedColumn = column[columnIndex];
    const updatedColumn = { ...resizedColumn, width };
    column[columnIndex] = updatedColumn;
    return [...column];
  });
};

export {
  dimensionalHeaderRow,
  dimensionalFooterRow,
  getDimensionalRows,
  getDimensionalColumns,
  getColumns,
  buildTree,
  reorderArray,
  getExpandedRows,
  handleCellChange,
  handleColumnResize,
  handleLocationChanged,
  handleCanReorderRows,
  buildCellReference,
};
